import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { listRows } from '@/api/supabaseClient';

export const PUBLIC_PAGE_DEFINITIONS = Object.freeze({
  home: { route: '/', label: 'Início' },
  exams: { route: '/exames', label: 'Exames', collection: true },
  schedule: { route: '/agendar', label: 'Agendar' },
  portal: { route: '/portal', label: 'Resultados de Exames' },
  insurance: { route: '/convenios', label: 'Convênios' },
  about: { route: '/sobre', label: 'Sobre' },
  contact: { route: '/contato', label: 'Contato' },
  faq: { route: '/faq', label: 'Perguntas frequentes' },
  blog: { route: '/blog', label: 'Blog', collection: true },
  doctors: { route: '/corpo-clinico', label: 'Corpo clínico' },
  careers: { route: '/trabalhe-conosco', label: 'Trabalhe Conosco' },
});

export const PUBLIC_PAGE_KEYS = Object.freeze(Object.keys(PUBLIC_PAGE_DEFINITIONS));

export function normalizePublicRoute(value) {
  const route = String(value || '').trim();
  if (!route || route === '/') return '/';
  return `/${route.replace(/^\/+|\/+$/g, '')}`;
}

export function buildFallbackPages() {
  return Object.fromEntries(PUBLIC_PAGE_KEYS.map(pageKey => [pageKey, {
    page_key: pageKey,
    ...PUBLIC_PAGE_DEFINITIONS[pageKey],
  }]));
}

export function inferPublicPageKey(row) {
  return PUBLIC_PAGE_DEFINITIONS[row?.page_key] ? row.page_key : null;
}

export function findRouteRedirect(pathname, pages, redirects) {
  const requested = normalizePublicRoute(pathname);
  for (const redirect of redirects) {
    const oldRoute = normalizePublicRoute(redirect.old_route);
    const current = pages[redirect.page_key];
    if (!current || current.route === oldRoute) continue;
    if (requested === oldRoute) return current.route;
    if (PUBLIC_PAGE_DEFINITIONS[redirect.page_key]?.collection && oldRoute !== '/' && requested.startsWith(`${oldRoute}/`)) {
      return `${current.route}${requested.slice(oldRoute.length)}`;
    }
  }
  return null;
}

const fallbackPages = buildFallbackPages();
const ROUTE_LOAD_TIMEOUT_MS = 4000;

function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('route map timeout')), ROUTE_LOAD_TIMEOUT_MS)),
  ]);
}

const PublicRoutesContext = createContext({
  ready: true,
  pages: fallbackPages,
  redirects: [],
  path: pageKey => fallbackPages[pageKey]?.route || '/',
  label: pageKey => fallbackPages[pageKey]?.label || pageKey,
  detailPath: (pageKey, slug) => `${fallbackPages[pageKey]?.route || ''}/${slug}`,
  resolvePath: value => value,
  labelForPath: (_value, fallback) => fallback,
  refresh: async () => {},
});

export function PublicRoutesProvider({ children }) {
  const [pages, setPages] = useState(fallbackPages);
  const [redirects, setRedirects] = useState([]);

  const refresh = useCallback(async () => {
    const [pageResult, redirectResult] = await Promise.allSettled([
      withTimeout(listRows('seo_pages', { orderBy: 'route', ascending: true, limit: 100 })),
      withTimeout(listRows('seo_route_redirects', { orderBy: 'old_route', ascending: true, limit: 500 })),
    ]);
    if (pageResult.status === 'fulfilled') {
      const configured = { ...fallbackPages };
      pageResult.value.forEach(row => {
        const pageKey = inferPublicPageKey(row);
        if (pageKey) {
          configured[pageKey] = {
            ...configured[pageKey],
            ...row,
            page_key: pageKey,
            route: normalizePublicRoute(row.route),
            label: pageKey === 'portal' ? PUBLIC_PAGE_DEFINITIONS.portal.label : row.label,
          };
        }
      });
      setPages(configured);
    }
    if (redirectResult.status === 'fulfilled') setRedirects(redirectResult.value);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { void refresh(); }, 2_500);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  const path = useCallback(pageKey => pages[pageKey]?.route || fallbackPages[pageKey]?.route || '/', [pages]);
  const label = useCallback(pageKey => pages[pageKey]?.label || fallbackPages[pageKey]?.label || pageKey, [pages]);
  const detailPath = useCallback((pageKey, slug) => `${path(pageKey).replace(/\/$/, '')}/${slug}`, [path]);
  const resolvePath = useCallback(value => {
    if (typeof value !== 'string' || !value.startsWith('/')) return value;
    const normalized = normalizePublicRoute(value);
    for (const pageKey of PUBLIC_PAGE_KEYS) {
      const fallback = fallbackPages[pageKey].route;
      if (normalized === fallback) return path(pageKey);
      if (PUBLIC_PAGE_DEFINITIONS[pageKey].collection && normalized.startsWith(`${fallback}/`)) {
        return `${path(pageKey)}${normalized.slice(fallback.length)}`;
      }
    }
    return value;
  }, [path]);
  const labelForPath = useCallback((value, fallback) => {
    if (typeof value !== 'string' || !value.startsWith('/')) return fallback;
    const normalized = normalizePublicRoute(value);
    const pageKey = PUBLIC_PAGE_KEYS.find(key => normalized === path(key) || normalized === fallbackPages[key].route);
    return pageKey ? label(pageKey) : fallback;
  }, [label, path]);

  const value = useMemo(() => ({ ready: true, pages, redirects, path, label, detailPath, resolvePath, labelForPath, refresh }), [detailPath, label, labelForPath, pages, path, redirects, refresh, resolvePath]);
  return <PublicRoutesContext.Provider value={value}>{children}</PublicRoutesContext.Provider>;
}

export function usePublicRoutes() {
  return useContext(PublicRoutesContext);
}
